// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 0356bd21-7ce3-4e39-8634-00a7b3b689ef

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Stack, TextField, InputAdornment, Tabs, Tab, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  FormControl, FormHelperText, InputLabel, Autocomplete, Alert, Tooltip, CircularProgress,
  Divider, ToggleButton, ToggleButtonGroup, Checkbox, Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory2';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import SendIcon from '@mui/icons-material/SendToMobile';
import { TypeBadge, TabLabel } from './ui';
import HelpBubble from './HelpBubble';
import CatalogInstallGuides from './CatalogInstallGuides';
import CatalogVersions from './CatalogVersions';
import store from './store';
import { getConfig } from './config';
import { getIdToken } from './auth';

// Optimum-only master catalog: canonical app definitions, cross-client
// adoption and name reconciliation. Backed by /catalog* on the API
// (catalog_app / catalog_question / catalog_alias, all master-only per
// 015_catalog.sql) rather than the browser's seeded localStorage — this
// screen is the one place that data is actually built and edited.
//
// NO server attachment, NO deployment link, NO heat-map anywhere here. A
// catalog entry describes a PRODUCT, reused across engagements; servers,
// deployments and heat-mapping are what a CLIENT's own fact sheet carries,
// never this screen — see backend/sql/015_catalog.sql for why.

const EMPTY_ENTRY = {
  id: null, name: '', vendorId: '', category: '', typicalHosting: '',
  defaultTime: '', default6R: '', description: '',
  lessonsLearned: '', awsGotchas: '', azureGotchas: '', questions: [],
  // Set when a vendor is typed rather than picked; created on save.
  vendorNewName: '',
};

export default function MasterCatalog() {
  const [tab, setTab] = useState(0);
  // Seeding a client from the catalog. Selection lives here rather than on each
  // row so "clone 40 of these into DFCI" is one action -- the case this exists
  // for is standing up a new client, not copying one entry at a time.
  const [picked, setPicked] = useState(() => new Set());
  // Selection in the RECONCILIATION tab, kept separate from `picked` (which is
  // catalogue entries for cloning). Two selections that mean different things
  // sharing one Set is the drift this file already pays for elsewhere.
  const [pickedRaw, setPickedRaw] = useState(() => new Set());
  const [promoting, setPromoting] = useState(false);
  // A half-filled version inside the entry dialog. See CatalogVersions: two
  // Save buttons on one screen meant the outer one silently discarded the
  // inner form.
  const pendingVersion = useRef(null);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneTenant, setCloneTenant] = useState('');
  const [cloneBusy, setCloneBusy] = useState(false);
  const [cloneResult, setCloneResult] = useState(null);
  // Destinations come from the store's tenant list, minus 'optimum' -- the
  // master workspace is the SOURCE and can never be a destination, which the
  // backend also refuses. Both ends, per non-negotiable 1.
  const [cloneTargets, setCloneTargets] = useState([]);
  useEffect(() => {
    let alive = true;
    store.listTenants()
      .then((ts) => { if (alive) setCloneTargets((ts || []).filter((t) => t && t.id && t.id !== 'optimum')); })
      .catch(() => { if (alive) setCloneTargets([]); });
    return () => { alive = false; };
  }, []);
  const [entries, setEntries] = useState([]);
  const [adoption, setAdoption] = useState([]);
  const [reference, setReference] = useState({ vendors: [], picklists: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const [unmappedOnly, setUnmappedOnly] = useState(true);

  const [editor, setEditor] = useState(null);   // { entry, pendingRawName? }
  const [mapDialog, setMapDialog] = useState(null); // { rawName, aliasId }
  const [confirmDelete, setConfirmDelete] = useState(null); // catalog entry id

  const runClone = async () => {
    if (!cloneTenant || !picked.size) return;
    setCloneBusy(true); setCloneResult(null);
    try {
      const out = await api('/catalog/clone', {
        method: 'POST',
        body: JSON.stringify({ tenantId: cloneTenant, catalogAppIds: [...picked] }),
      });
      // Report what actually happened rather than "done": a clone that skipped
      // everything because the entries were already there is a SUCCESS, and
      // silently implying it created them is how someone clones twice.
      setCloneResult(out);
      setPicked(new Set());
    } catch (e) { setErr(e.message); } finally { setCloneBusy(false); }
  };

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope is often an HTML page, not JSON — the CloudFront
    // rewrite-to-200 trap this codebase keeps rediscovering. Parse defensively
    // so a mis-routed request reads as "request failed (404)" rather than an
    // opaque "Unexpected token <".
    let body = {};
    let parseFailed = false;
    try { body = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(body.error || body.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return body;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [cat, adopt, ref] = await Promise.all([
        api('/catalog'), api('/catalog/adoption'), api('/reference'),
      ]);
      setEntries(cat);
      setAdoption(adopt);
      setReference({ vendors: ref.vendors || [], picklists: ref.picklists || {} });
    } catch (e) {
      setErr(e.message || 'failed to load the catalog');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const picklistValues = (key) => ((reference.picklists[key] || {}).values || [])
    .filter((v) => v.active !== false).map((v) => v.label || v.value);

  // Versions are searchable, not just names. Frank: "we need a search button so
  // we can look at a list of 200 apps and I'm looking for like citrix, then it
  // shows each of the versions we have." Matching the version strings means
  // typing a release number finds the product that has it, which is the other
  // half of the same question.
  const filteredEntries = entries.filter((r) => {
    const needle = q.toLowerCase();
    if (!needle) return true;
    return r.name.toLowerCase().includes(needle)
      || (r.vendor || '').toLowerCase().includes(needle)
      || (r.versions || '').toLowerCase().includes(needle)
      || (r.category || '').toLowerCase().includes(needle);
  });
  const filteredAdoption = adoption.filter((r) => (!unmappedOnly || !r.mapped)
    && r.rawName.toLowerCase().includes(q.toLowerCase()));
  // What select-all may actually take: visible AND not already mapped.
  // Including mapped rows would put names in the selection that the action then
  // skips, and a count that does not match what happens is how people stop
  // trusting the button.
  const selectableRaw = filteredAdoption.filter((r) => !r.mapped);

  // ---- Entry editor ---------------------------------------------------------
  // Promoting a raw name used to open a blank form with only the name filled,
  // so the category and hosting the client fact sheets already carried had to
  // be retyped from memory. The adoption report now returns what those sheets
  // actually say, and the majority answer is offered as a starting point.
  //
  // Offered, not asserted: `observedNote` below puts the evidence on screen
  // ("hosting: SaaS on 4 of 5") so the person saving knows this came from
  // counting deployments rather than from anywhere authoritative, and can
  // change it. Non-negotiable 8 forbids a MODEL drafting hosting because that
  // is guessing at a client's estate; counting what clients recorded and
  // showing the count is the opposite of a guess.
  const openNewEntry = (prefillName) => {
    const row = adoption.find((r) => r.rawName === prefillName);
    const obs = (row && row.observed) || {};
    // The observed value has to be matched back to the picklist before it can
    // be prefilled. Imports wrote hostingType as "onPremise" while the picklist
    // says "On Premise", and a Select handed a value that is not one of its
    // options renders EMPTY -- which would look exactly like the prefill not
    // working, for the majority of rows. Compared case-insensitively with
    // non-alphanumerics stripped, so "onPremise", "On-Prem" and "On Premise"
    // all land on the option that actually exists.
    const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const toOption = (attr, picklistKey) => {
      const list = obs[attr] || [];
      const options = picklistValues(picklistKey);
      for (const o of list) {
        const hit = options.find((opt) => norm(opt) === norm(o.value));
        if (hit) return hit;
      }
      return '';
    };
    const top = (attr) => (obs[attr] && obs[attr][0] ? obs[attr][0].value : '');
    const vendorName = top('vendor');
    const matchedVendor = vendorName
      ? (reference.vendors || []).find((v) => (v.name || '').toLowerCase() === vendorName.toLowerCase())
      : null;
    setEditor({
      entry: {
        ...EMPTY_ENTRY,
        name: prefillName || '',
        category: toOption('applicationType', 'applicationType'),
        typicalHosting: toOption('hostingType', 'hostingType'),
        // A vendor name the clients recorded that does not yet exist as a
        // vendor record is kept as free text; saving creates it.
        vendorId: matchedVendor ? matchedVendor.id : '',
        vendorNewName: matchedVendor ? '' : vendorName || '',
      },
      pendingRawName: prefillName || null,
      observed: obs,
    });
  };

  // Rendered under the prefilled fields so the user can see the evidence.
  const observedNote = (attr, label) => {
    const list = editor && editor.observed && editor.observed[attr];
    if (!list || !list.length) return null;
    return `${label}: ${list.map((o) => `${o.value} on ${o.count}`).join(', ')}`;
  };
  const openEditEntry = async (id) => {
    try {
      const full = await api(`/catalog?id=${encodeURIComponent(id)}`);
      setEditor({ entry: full, pendingRawName: null });
    } catch (e) { setErr(e.message); }
  };

  const saveEntry = async () => {
    const { entry, pendingRawName } = editor;
    if (!entry.name || !entry.name.trim()) { setErr('a name is required'); return; }
    try {
      // A vendor typed rather than picked is created first, so the catalogue
      // entry has a real id to reference. PUT /reference/vendor is master-only
      // in the handler, which this screen already is.
      let vendorId = entry.vendorId;
      if (!vendorId && entry.vendorNewName && entry.vendorNewName.trim()) {
        const created = await api('/reference/vendor', {
          method: 'PUT', body: JSON.stringify({ name: entry.vendorNewName.trim() }),
        });
        vendorId = created.id || (created.rows && created.rows[0] && created.rows[0].id);
      }
      const payload = { ...entry, vendorId: vendorId || '' };
      delete payload.vendorNewName;
      const { id } = await api('/catalog', { method: 'PUT', body: JSON.stringify(payload) });
      // Any questions edited in this session that are not yet persisted (no
      // real uuid id) are saved after the entry exists, since they carry its id.
      for (const question of entry.questions || []) {
        if (question._dirty || !question.id) {
          await api('/catalog/questions', {
            method: 'PUT',
            body: JSON.stringify({ ...question, catalogAppId: id }),
          });
        }
      }
      // "Promote to new entry" from Reconciliation: the entry did not exist
      // until this save, so the alias mapping happens in the same action
      // rather than a second trip the user would have to remember to make.
      if (pendingRawName) {
        await api('/catalog/aliases', { method: 'PUT', body: JSON.stringify({ rawName: pendingRawName, catalogAppId: id }) });
      }
      // Flush a half-filled version before the dialog closes. Unconditional
      // because flush() is a no-op when there is nothing to save, and because
      // the alternative -- deciding here whether the inner form "counts" -- is
      // how the draft got dropped in the first place.
      let savedVersion = false;
      if (pendingVersion.current && pendingVersion.current.flush) {
        try { savedVersion = await pendingVersion.current.flush(); }
        catch (e) { setErr(`Saved ${entry.name}, but the version could not be saved: ${e.message}`); }
      }
      setMsg(`Saved ${entry.name}${savedVersion ? ' and its version' : ''}.`);
      setEditor(null);
      await load();
    } catch (e) { setErr(e.message); }
  };

  // Promote every selected raw name to its own catalogue entry, carrying the
  // category and hosting the client fact sheets already recorded.
  //
  // Deliberately NOT a merge. Measuring the 650 showed that conservative
  // normalisation collapses three of them and that the near-miss families are
  // genuinely different products, so one-name-one-entry is both the safe
  // operation and the correct one.
  const promoteSelected = async () => {
    if (!pickedRaw.size) return;
    setPromoting(true);
    try {
      const names = [...pickedRaw];
      // The observed attributes travel with the request so the server does not
      // recompute an aggregate the screen is already holding.
      const observed = {};
      names.forEach((n) => {
        const row = adoption.find((r) => r.rawName === n);
        const obs = (row && row.observed) || {};
        const pick = (attr, picklistKey) => {
          const options = picklistValues(picklistKey);
          const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          for (const o of obs[attr] || []) {
            const hit = options.find((opt) => norm(opt) === norm(o.value));
            if (hit) return hit;
          }
          return null;
        };
        observed[n] = {
          category: pick('applicationType', 'applicationType'),
          typicalHosting: pick('hostingType', 'hostingType'),
        };
      });
      // Batched to the server's cap. A single request holding all 650 would run
      // 1,300 sequential statements inside one 30-second Lambda and time out
      // halfway, leaving a partial write nobody can see.
      const BATCH = 50;
      let created = 0; let skipped = 0; const failed = [];
      for (let i = 0; i < names.length; i += BATCH) {
        const slice = names.slice(i, i + BATCH);
        const out = await api('/catalog/promote', {
          method: 'POST',
          body: JSON.stringify({ rawNames: slice, observed }),
        });
        created += (out.created || []).length;
        skipped += (out.skipped || []).length;
        failed.push(...(out.failed || []));
      }
      // Reported honestly, including the failures. Saying "done" over a partial
      // result is what makes a queue untrustworthy.
      setMsg(`Promoted ${created} name${created === 1 ? '' : 's'}`
        + (skipped ? `, skipped ${skipped} already mapped` : '')
        + (failed.length ? `, ${failed.length} failed` : '') + '.');
      if (failed.length) setErr(`Could not promote: ${failed.map((f) => f.rawName).join(', ')}`);
      setPickedRaw(new Set());
      await load();
    } catch (e) {
      setErr(e.message || 'bulk promote failed');
    } finally {
      setPromoting(false);
    }
  };

  const deleteEntry = async (id) => {
    try {
      await api(`/catalog?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      setMsg('Catalog entry removed.');
      setConfirmDelete(null);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const deleteQuestionLocally = (idx) => setEditor((cur) => ({
    ...cur, entry: { ...cur.entry, questions: cur.entry.questions.filter((_, i) => i !== idx) },
  }));
  const addQuestion = () => setEditor((cur) => ({
    ...cur, entry: { ...cur.entry, questions: [...(cur.entry.questions || []), { question: '', category: '', sortOrder: (cur.entry.questions || []).length, _dirty: true }] },
  }));
  const editQuestion = (idx, patch) => setEditor((cur) => {
    const questions = cur.entry.questions.slice();
    questions[idx] = { ...questions[idx], ...patch, _dirty: true };
    return { ...cur, entry: { ...cur.entry, questions } };
  });
  const removeQuestion = async (idx) => {
    const question = editor.entry.questions[idx];
    if (question.id) {
      try { await api(`/catalog/questions?id=${encodeURIComponent(question.id)}`, { method: 'DELETE' }); } catch (e) { setErr(e.message); return; }
    }
    deleteQuestionLocally(idx);
  };

  // ---- Reconciliation actions ------------------------------------------------
  const mapToExisting = async (rawName, catalogAppId) => {
    try {
      await api('/catalog/aliases', { method: 'PUT', body: JSON.stringify({ rawName, catalogAppId }) });
      setMsg(`Mapped "${rawName}".`);
      setMapDialog(null);
      await load();
    } catch (e) { setErr(e.message); }
  };
  const unmap = async (aliasId, rawName) => {
    try {
      await api(`/catalog/aliases?id=${encodeURIComponent(aliasId)}`, { method: 'DELETE' });
      setMsg(`"${rawName}" is unmapped again.`);
      await load();
    } catch (e) { setErr(e.message); }
  };

  const vendorOptions = reference.vendors || [];

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <InventoryIcon sx={{ color: 'sectionHeader.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Master Application Catalog</Typography>
        <HelpBubble topic="masterCatalog" />
        <Chip size="small" label="Optimum-owned · reused across clients" sx={{ bgcolor: '#eef3ff', color: '#0B5FFF' }} />
        <Box sx={{ flex: 1 }} />
        {tab !== 2 && (
          <TextField size="small" placeholder={tab === 0 ? 'Search name, vendor, category or version…' : 'Search raw names…'} value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        )}
        {tab === 0 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openNewEntry()}>New entry</Button>
        )}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {/* Shown only with a selection: an always-present "clone" bar on a
          master-only screen invites clicking it with nothing chosen. */}
      {picked.size > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}
          action={<Button size="small" variant="contained" onClick={() => { setCloneResult(null); setCloneOpen(true); }}>
            Add to a client&hellip;</Button>}>
          {picked.size} catalog entr{picked.size === 1 ? 'y' : 'ies'} selected.
        </Alert>
      )}

      <Dialog open={cloneOpen} onClose={() => !cloneBusy && setCloneOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Add to a client workspace <HelpBubble topic="cloneCatalogToClient" />
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Creates an Application fact sheet in the chosen workspace for each selected catalog
            entry, carrying only the shared product details &mdash; name, vendor, category, typical
            hosting and the default TIME / 6R. Optimum&rsquo;s own notes, lessons learned and
            discovery questions are never copied.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="clone-target">Client workspace</InputLabel>
            <Select labelId="clone-target" label="Client workspace" value={cloneTenant}
              onChange={(e) => setCloneTenant(e.target.value)}>
              {cloneTargets.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name || t.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Running this twice is safe &mdash; an entry already present in that workspace is skipped
            rather than duplicated.
          </Typography>
          {cloneResult && (
            <Alert severity={cloneResult.created ? 'success' : 'info'} sx={{ mt: 2 }}>
              Created {cloneResult.created}, skipped {cloneResult.skipped}.
              {!!cloneResult.skipped && (
                <ul style={{ margin: '4px 0 0 16px' }}>
                  {(cloneResult.rows || []).filter((r) => r.status === 'skipped').slice(0, 6)
                    .map((r) => <li key={r.catalogAppId}>{r.name} &mdash; {r.reason}</li>)}
                </ul>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneOpen(false)} disabled={cloneBusy}>Close</Button>
          <Button variant="contained" onClick={runClone} disabled={cloneBusy || !cloneTenant || !picked.size}>
            Add {picked.size} to client
          </Button>
        </DialogActions>
      </Dialog>

      <Tabs value={tab} onChange={(e, v) => { setTab(v); setQ(''); }} sx={{ mb: 2, borderBottom: '1px solid #E0E6ED' }}>
        <Tab label={<TabLabel text={`Catalog (${entries.length})`} topic="tabCatalogEntries" />} />
        <Tab label={<TabLabel text={`Reconciliation (${adoption.filter((r) => !r.mapped).length} unmapped)`} topic="tabReconciliation" />} />
        <Tab label={<TabLabel text="Installation guides" topic="tabInstallGuides" />} />
      </Tabs>

      {tab === 0 && (
        <>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Canonical definitions built once. Knowledge and discovery questions here are Optimum-only and never shown
            to a client; a client's own fact sheet stays a separate, tenant-scoped record even when it names the same
            application.
          </Typography>
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={picked.size > 0 && picked.size < filteredEntries.length}
                      checked={filteredEntries.length > 0 && picked.size === filteredEntries.length}
                      onChange={(e) => setPicked(e.target.checked
                        ? new Set(filteredEntries.map((x) => x.id)) : new Set())}
                    />
                  </TableCell>
                  <TableCell>Application</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Typical Hosting</TableCell>
                  <TableCell>Default TIME / 6R</TableCell>
                  <TableCell>Versions</TableCell>
                  <TableCell>Aliases</TableCell>
                  <TableCell>Questions</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEntries.map((r) => (
                  <TableRow key={r.id} hover selected={picked.has(r.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={picked.has(r.id)} onChange={(e) => {
                        const next = new Set(picked);
                        if (e.target.checked) next.add(r.id); else next.delete(r.id);
                        setPicked(next);
                      }} />
                    </TableCell>
                    <TableCell>
                      {/* The name opens the entry. Frank, 2026-08-19: "I need to
                          be able to click an app in the master catalog by just
                          clicking the name." The pencil in the Actions column
                          was the only way in, which is a target the width of an
                          icon at the far right of a nine-column table, while
                          the obvious thing to click did nothing.
                          Rendered as a button rather than an onClick on the
                          cell so it reaches the keyboard and announces itself
                          as something that can be activated. */}
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TypeBadge type="Application" size={18} />
                        <Link component="button" type="button" underline="hover"
                          onClick={() => openEditEntry(r.id)}
                          sx={{ fontWeight: 700, textAlign: 'left', p: 0,
                            font: 'inherit', color: 'primary.main', cursor: 'pointer' }}>
                          {r.name}
                        </Link>
                      </Stack>
                      {r.description && <Typography variant="caption" sx={{ color: '#888' }}>{r.description}</Typography>}
                    </TableCell>
                    <TableCell>{r.vendor || <span style={{ color: '#bbb' }}>—</span>}</TableCell>
                    <TableCell>{r.category || <span style={{ color: '#bbb' }}>—</span>}</TableCell>
                    <TableCell>{r.typicalHosting || <span style={{ color: '#bbb' }}>—</span>}</TableCell>
                    <TableCell>
                      {r.defaultTime && <Chip size="small" label={r.defaultTime} sx={{ mr: 0.5 }} />}
                      {r.default6R && <Chip size="small" variant="outlined" label={r.default6R} />}
                      {!r.defaultTime && !r.default6R && <span style={{ color: '#bbb' }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      {/* The releases themselves, not a count. A count tells you
                          there are four; the point of the column is to see
                          WHICH four without opening the entry. Clicking opens
                          it, since editing them is the next thing you want. */}
                      {r.versionCount ? (
                        <Link component="button" type="button" underline="hover"
                          onClick={() => openEditEntry(r.id)}
                          sx={{ font: 'inherit', fontSize: 12, p: 0, textAlign: 'left',
                            color: 'primary.main', cursor: 'pointer' }}>
                          {r.versions}
                          {r.versionCount > 6 ? ` +${r.versionCount - 6} more` : ''}
                        </Link>
                      ) : (
                        <span style={{ color: '#bbb' }}>—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={r.aliasCount > 0 ? 'primary' : 'default'} label={r.aliasCount} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={r.questionCount} />
                    </TableCell>
                    <TableCell align="right">
                      {/* A per-row action as well as the bulk bar. Selection-only
                          was a discoverability failure: with nothing ticked the
                          screen showed no way to send an entry to a client, so
                          the feature was invisible to someone looking straight
                          at it. The bulk path stays for standing up a new
                          client; this is for the single-app case. */}
                      <Tooltip title="Add to a client workspace">
                        <IconButton size="small" onClick={() => {
                          setPicked(new Set([r.id])); setCloneResult(null); setCloneOpen(true);
                        }}><SendIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditEntry(r.id)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => setConfirmDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!filteredEntries.length && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ color: '#999', py: 3 }}>No catalog entries yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {tab === 2 && <CatalogInstallGuides api={api} entries={entries} />}

      {tab === 1 && (
        <>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#666', flex: 1 }}>
              Every distinct Application name across every client, aggregated per tenant so no client's adoption count
              ever blends with another's. Map a raw name to an existing canonical entry, or promote it to a new one.
            </Typography>
            <ToggleButtonGroup size="small" exclusive value={unmappedOnly ? 'unmapped' : 'all'}
              onChange={(e, v) => v && setUnmappedOnly(v === 'unmapped')}>
              <ToggleButton value="unmapped">Unmapped only</ToggleButton>
              <ToggleButton value="all">All names</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {pickedRaw.size > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                <b>{pickedRaw.size}</b> name{pickedRaw.size === 1 ? '' : 's'} selected. Each becomes its
                own catalogue entry, carrying the category and hosting its client records already show.
                Nothing is merged.
              </Typography>
              <Button size="small" onClick={() => setPickedRaw(new Set())} disabled={promoting}>Clear</Button>
              <Button size="small" variant="contained" startIcon={<UpgradeIcon />}
                onClick={promoteSelected} disabled={promoting}>
                {promoting ? 'Promoting…' : `Promote ${pickedRaw.size}`}
              </Button>
            </Paper>
          )}
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={pickedRaw.size > 0 && pickedRaw.size < selectableRaw.length}
                      checked={selectableRaw.length > 0 && pickedRaw.size === selectableRaw.length}
                      onChange={(e) => setPickedRaw(e.target.checked
                        ? new Set(selectableRaw.map((r) => r.rawName))
                        : new Set())}
                    />
                  </TableCell>
                  <TableCell>Raw application name</TableCell>
                  <TableCell>Instances</TableCell>
                  <TableCell>Clients</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAdoption.map((r) => (
                  <TableRow key={r.rawName} hover selected={pickedRaw.has(r.rawName)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        disabled={r.mapped}
                        checked={pickedRaw.has(r.rawName)}
                        onChange={(e) => setPickedRaw((cur) => {
                          const next = new Set(cur);
                          if (e.target.checked) next.add(r.rawName); else next.delete(r.rawName);
                          return next;
                        })}
                      />
                    </TableCell>
                    <TableCell>
                      {/* Same rule as the catalogue tab: the name is the thing
                          people aim at. Where a raw name is already mapped it
                          opens the entry it maps to; where it is not, it opens
                          the promote form prefilled, which is the only action
                          available for it anyway. */}
                      <Link component="button" type="button" underline="hover"
                        onClick={() => (r.mapped
                          ? openEditEntry(r.catalogAppId)
                          : openNewEntry(r.rawName))}
                        sx={{ fontWeight: 700, textAlign: 'left', p: 0,
                          font: 'inherit', color: 'primary.main', cursor: 'pointer' }}>
                        {r.rawName}
                      </Link>
                    </TableCell>
                    <TableCell>{r.totalCount}</TableCell>
                    <TableCell>
                      <Chip size="small" color={r.tenantCount > 1 ? 'primary' : 'default'} label={`${r.tenantCount} tenant${r.tenantCount === 1 ? '' : 's'}`} />
                    </TableCell>
                    <TableCell>
                      {r.mapped
                        ? <Chip size="small" sx={{ bgcolor: '#e8f5e9', color: '#1b5e20' }} label={`→ ${r.canonicalName}`} />
                        : <Chip size="small" sx={{ bgcolor: '#fff3e0', color: '#8a5a00' }} label="Unmapped" />}
                    </TableCell>
                    <TableCell align="right">
                      {r.mapped ? (
                        <>
                          <Tooltip title="Change mapping"><IconButton size="small" onClick={() => setMapDialog({ rawName: r.rawName })}><LinkIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Unmap"><IconButton size="small" onClick={() => unmap(r.aliasId, r.rawName)}><LinkOffIcon fontSize="small" /></IconButton></Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Map to existing entry"><IconButton size="small" onClick={() => setMapDialog({ rawName: r.rawName })}><LinkIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Promote to new entry"><IconButton size="small" onClick={() => openNewEntry(r.rawName)}><UpgradeIcon fontSize="small" /></IconButton></Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!filteredAdoption.length && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ color: '#999', py: 3 }}>Nothing to reconcile.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* ---- Map-to-existing dialog ---- */}
      {mapDialog && (
        <Dialog open onClose={() => setMapDialog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Map "{mapDialog.rawName}"</DialogTitle>
          <DialogContent>
            <Autocomplete
              autoFocus
              options={entries}
              getOptionLabel={(o) => o.name || ''}
              onChange={(e, val) => val && mapToExisting(mapDialog.rawName, val.id)}
              renderInput={(params) => <TextField {...params} label="Canonical catalog entry" margin="normal" />}
            />
          </DialogContent>
          <DialogActions><Button onClick={() => setMapDialog(null)}>Cancel</Button></DialogActions>
        </Dialog>
      )}

      {/* ---- Delete confirm ---- */}
      {confirmDelete && (
        <Dialog open onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Remove this catalog entry?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Its discovery questions and raw-name mappings go with it; any client fact sheet referencing it is left
              untouched but loses the link.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button color="error" onClick={() => deleteEntry(confirmDelete)}>Remove</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ---- Entry editor ---- */}
      {editor && (
        <Dialog open onClose={() => setEditor(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editor.entry.id ? `Edit ${editor.entry.name}` : 'New catalog entry'}
            {editor.pendingRawName && (
              <Typography variant="caption" display="block" sx={{ color: '#666', fontWeight: 400 }}>
                Will be mapped to raw name "{editor.pendingRawName}" on save.
              </Typography>
            )}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Details</Typography>
            <Stack spacing={2} sx={{ mb: 3 }}>
              <TextField label="Name" required fullWidth value={editor.entry.name}
                onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, name: e.target.value } }))} />
              <Stack direction="row" spacing={2}>
                {/* freeSolo, because the vendor list holds six records and the
                    catalogue is being built out of 650 raw names. Sending
                    someone to Administration to add "Imprivata" before they can
                    finish promoting it is how a queue stops being worked.
                    A typed name that matches nothing is created on save. */}
                <Autocomplete
                  freeSolo fullWidth options={vendorOptions}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.name || '')}
                  value={vendorOptions.find((v) => v.id === editor.entry.vendorId)
                    || editor.entry.vendorNewName || null}
                  onChange={(e, val) => setEditor((c) => ({
                    ...c,
                    entry: typeof val === 'string'
                      ? { ...c.entry, vendorId: '', vendorNewName: val }
                      : { ...c.entry, vendorId: val ? val.id : '', vendorNewName: '' },
                  }))}
                  onInputChange={(e, val, reason) => {
                    if (reason !== 'input') return;
                    const hit = vendorOptions.find((v) => (v.name || '').toLowerCase() === val.toLowerCase());
                    setEditor((c) => ({
                      ...c,
                      entry: hit
                        ? { ...c.entry, vendorId: hit.id, vendorNewName: '' }
                        : { ...c.entry, vendorId: '', vendorNewName: val },
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Vendor"
                      helperText={editor.entry.vendorNewName
                        ? `"${editor.entry.vendorNewName}" will be created as a new vendor on save`
                        : observedNote('vendor', 'clients recorded') || ' '} />
                  )}
                />
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select label="Category" value={editor.entry.category || ''}
                    onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, category: e.target.value } }))}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {picklistValues('applicationType').map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                  {observedNote('applicationType', 'clients recorded') && (
                    <FormHelperText>{observedNote('applicationType', 'clients recorded')}</FormHelperText>
                  )}
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Typical Hosting</InputLabel>
                  <Select label="Typical Hosting" value={editor.entry.typicalHosting || ''}
                    onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, typicalHosting: e.target.value } }))}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {picklistValues('hostingType').map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                  {observedNote('hostingType', 'clients recorded') && (
                    <FormHelperText>{observedNote('hostingType', 'clients recorded')}</FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Default TIME</InputLabel>
                  <Select label="Default TIME" value={editor.entry.defaultTime || ''}
                    onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, defaultTime: e.target.value } }))}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {picklistValues('time').map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Default 6R</InputLabel>
                  <Select label="Default 6R" value={editor.entry.default6R || ''}
                    onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, default6R: e.target.value } }))}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {picklistValues('sixR').map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>
              <TextField label="Description" fullWidth multiline minRows={2} value={editor.entry.description || ''}
                onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, description: e.target.value } }))} />
            </Stack>

            <Divider sx={{ mb: 2 }} />
            {/* Versions sit above Knowledge because they are facts about the
                product with dates attached, while Knowledge is Optimum's
                commentary. Two different origins, and the order says so. */}
            <Box sx={{ mb: 3 }}>
              <CatalogVersions api={api} catalogAppId={editor.entry.id} appName={editor.entry.name}
                pendingRef={pendingVersion} />
            </Box>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Knowledge <HelpBubble topic="catalogLessonsLearned" />
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              De-identified only — no client names, hostnames or counts. This text is reused across every client
              engagement.
            </Alert>
            <Stack spacing={2} sx={{ mb: 3 }}>
              <TextField label="Lessons Learned" fullWidth multiline minRows={2} value={editor.entry.lessonsLearned || ''}
                onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, lessonsLearned: e.target.value } }))} />
              <TextField label="AWS Configuration Gotchas" fullWidth multiline minRows={2} value={editor.entry.awsGotchas || ''}
                onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, awsGotchas: e.target.value } }))} />
              <TextField label="Azure Configuration Gotchas" fullWidth multiline minRows={2} value={editor.entry.azureGotchas || ''}
                onChange={(e) => setEditor((c) => ({ ...c, entry: { ...c.entry, azureGotchas: e.target.value } }))} />
            </Stack>

            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                Discovery questions <HelpBubble topic="catalogQuestions" />
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addQuestion}>Add question</Button>
            </Stack>
            <Stack spacing={1}>
              {(editor.entry.questions || []).map((question, idx) => (
                <Stack key={question.id || `new-${idx}`} direction="row" spacing={1} alignItems="center">
                  <TextField size="small" fullWidth placeholder="Question text" value={question.question || ''}
                    onChange={(e) => editQuestion(idx, { question: e.target.value })} />
                  <TextField size="small" sx={{ width: 200 }} placeholder="Category (e.g. hosting, security)"
                    value={question.category || ''} onChange={(e) => editQuestion(idx, { category: e.target.value })} />
                  <IconButton size="small" onClick={() => removeQuestion(idx)}><DeleteIcon fontSize="small" /></IconButton>
                </Stack>
              ))}
              {!(editor.entry.questions || []).length && (
                <Typography variant="caption" sx={{ color: '#999' }}>No discovery questions yet.</Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditor(null)}>Cancel</Button>
            <Button variant="contained" onClick={saveEntry}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
